import React from 'react';
import PropTypes from 'prop-types';
import { useParams, useSearchParams } from 'react-router-dom-v5-compat';

import {
  CHAMPVA_PROVIDERS,
  buildDateFormatter,
  isEightStepPensionClaim,
} from '~/platform/shared/claims-appeals';
import { isSafeUrl } from '../../utils/helpers';
import { getClaimPhases, getPensionClaimPhases } from '../../utils/claimPhase';
import {
  claimTabLinkProps,
  knownClaimTab,
} from '../../utils/claim-tab-helpers';
import BasicLink from '../web-component-wrappers/BasicLink';
import MovedBackEvidenceAlert from '../MovedBackEvidenceAlert';

const isRelativeUrl = url =>
  typeof url === 'string' && (url.startsWith('/') || url.startsWith('../'));

export default function ClaimPhaseStepper({
  claimDate,
  currentClaimPhaseDate,
  currentPhase,
  currentPhaseBack,
  claimTypeCode,
  customSteps,
  currentStepPrefix,
  useVaTabs = false,
  showCurrentPhaseDate = true,
}) {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const linkAnalyticsProps = CHAMPVA_PROVIDERS.has(typeParam)
    ? {}
    : { 'disable-analytics': true };
  const typeQuery = typeParam ? `?type=${typeParam}` : '';
  const { id: claimId } = useParams();

  // API-supplied relative links: '../<tab>' maps to the canonical ?tab= URL
  // flag-on; other relative values are depth-compensated to the claim root.
  const stepLinkProps = url => {
    if (!useVaTabs || !url.startsWith('../')) {
      return { path: `${url}${typeQuery}` };
    }
    const rest = url.slice(3);
    const suffixStart = rest.search(/[?#]/);
    const base = suffixStart === -1 ? rest : rest.slice(0, suffixStart);
    const suffix = suffixStart === -1 ? '' : rest.slice(suffixStart);
    if (knownClaimTab(base)) {
      return claimTabLinkProps(base, {
        claimId,
        search: suffix.startsWith('?') ? suffix : typeQuery,
        hash: suffix.startsWith('#') ? suffix : '',
      });
    }
    return { path: `/your-claims/${claimId}/${rest}${typeQuery}` };
  };

  const formattedClaimDate = buildDateFormatter()(claimDate);
  const formattedCurrentClaimPhaseDate = buildDateFormatter()(
    currentClaimPhaseDate || claimDate,
  );

  let claimPhases;
  if (customSteps?.length > 0) {
    claimPhases = customSteps.map((step, index) => ({
      phase: step.phase || index + 1,
      header: step.header,
      description: step.description,
      descriptionDateTemplate: step.descriptionDateTemplate || null,
      details: step.details,
      linkText: step.linkText,
      linkUrl: step.linkUrl,
      noteText: step.noteText || null,
      uploadLinkText: step.uploadLinkText || null,
      uploadLinkUrl: step.uploadLinkUrl || null,
      repeatable: !!step.repeatable,
    }));
  } else if (isEightStepPensionClaim(claimTypeCode)) {
    claimPhases = getPensionClaimPhases(formattedClaimDate, {
      claimId,
      useVaTabs,
    });
  } else {
    claimPhases = getClaimPhases(formattedClaimDate, { claimId, useVaTabs });
  }
  const finalPhase = claimPhases.length;

  const isCurrentPhase = phase => {
    return phase === currentPhase;
  };
  const isCurrentPhaseAndNotFinalPhase = phase => {
    return isCurrentPhase(phase) && phase !== finalPhase;
  };
  const isPhaseComplete = phase => {
    return (
      phase < currentPhase || (isCurrentPhase(phase) && phase === finalPhase)
    );
  };
  const phaseCanRepeat = claimPhase => {
    if (customSteps?.length > 0) {
      return claimPhase.repeatable;
    }
    return claimPhase.repeatable || [3, 4, 5, 6].includes(claimPhase.phase);
  };

  let headerIconAttributes = {};
  const showIcon = phase => {
    if (isCurrentPhaseAndNotFinalPhase(phase)) {
      // Set headerIcon object
      headerIconAttributes = {
        icon: 'flag',
        text: 'Current',
        class: 'phase-current',
      };
      return true;
    }

    if (isPhaseComplete(phase)) {
      // Set headerIcon object
      headerIconAttributes = {
        icon: 'check_circle',
        text: 'Completed',
        class: 'phase-complete',
      };
      return true;
    }

    return false;
  };

  return (
    <div className="claim-phase-stepper">
      <va-accordion>
        {claimPhases.map((claimPhase, phaseIndex) => {
          const linkEl =
            claimPhase.linkText && isSafeUrl(claimPhase.linkUrl) ? (
              <p>
                {isRelativeUrl(claimPhase.linkUrl) ? (
                  <BasicLink
                    active
                    {...linkAnalyticsProps}
                    {...stepLinkProps(claimPhase.linkUrl)}
                    text={claimPhase.linkText}
                  />
                ) : (
                  <va-link
                    {...linkAnalyticsProps}
                    href={claimPhase.linkUrl}
                    text={claimPhase.linkText}
                  />
                )}
              </p>
            ) : null;
          // customSteps = CHAMPVA config: link before details (matches Figma mock).
          // Standard claims: details before link (original order).
          const isCustom = customSteps?.length > 0;
          return (
            <va-accordion-item
              key={phaseIndex}
              header={claimPhase.header}
              level={3}
              id={`phase${claimPhase.phase}`}
              open={isCurrentPhase(claimPhase.phase)}
            >
              {showIcon(claimPhase.phase) && (
                <va-icon
                  icon={headerIconAttributes.icon}
                  class={headerIconAttributes.class}
                  srtext={headerIconAttributes.text}
                  slot="icon"
                />
              )}
              {isCurrentPhase(claimPhase.phase) && showCurrentPhaseDate && (
                <strong className="current-phase">
                  {currentStepPrefix || 'Your claim is in this step as of'}{' '}
                  {formattedCurrentClaimPhaseDate}.
                </strong>
              )}
              {isCurrentPhase(claimPhase.phase) && currentPhaseBack && (
                <MovedBackEvidenceAlert />
              )}
              {(!isCurrentPhase(claimPhase.phase) || !currentPhaseBack) &&
                phaseCanRepeat(claimPhase) && (
                  <div className="repeat-phase">
                    <va-icon icon="autorenew" size={3} aria-hidden="true" />
                    <span>Step may repeat if we need more information.</span>
                  </div>
                )}
              <p className="vads-u-margin-y--0">
                {claimPhase.descriptionDateTemplate
                  ? claimPhase.descriptionDateTemplate.replace(
                      '{date}',
                      formattedClaimDate,
                    )
                  : claimPhase.description}
              </p>
              {/* customSteps = CHAMPVA: link then details (matches Figma mock).
                Standard claims: details then link (original order). */}
              {isCustom && linkEl}
              {claimPhase.details?.map((detail, detailIndex) => (
                <p key={detailIndex}>{detail}</p>
              ))}
              {!isCustom && linkEl}
              {claimPhase.noteText && (
                <p>
                  <strong>Note:</strong> {claimPhase.noteText}
                </p>
              )}
              {claimPhase.uploadLinkText &&
                isSafeUrl(claimPhase.uploadLinkUrl) && (
                  <p>
                    {isRelativeUrl(claimPhase.uploadLinkUrl) ? (
                      <BasicLink
                        active
                        {...linkAnalyticsProps}
                        {...stepLinkProps(claimPhase.uploadLinkUrl)}
                        text={claimPhase.uploadLinkText}
                      />
                    ) : (
                      <va-link
                        {...linkAnalyticsProps}
                        external
                        href={claimPhase.uploadLinkUrl}
                        text={claimPhase.uploadLinkText}
                      />
                    )}
                  </p>
                )}
            </va-accordion-item>
          );
        })}
      </va-accordion>
    </div>
  );
}

ClaimPhaseStepper.propTypes = {
  claimDate: PropTypes.string.isRequired,
  currentPhase: PropTypes.number.isRequired,
  claimTypeCode: PropTypes.string,
  currentClaimPhaseDate: PropTypes.string,
  currentPhaseBack: PropTypes.bool,
  currentStepPrefix: PropTypes.string,
  customSteps: PropTypes.arrayOf(
    PropTypes.shape({
      phase: PropTypes.number,
      header: PropTypes.string,
      description: PropTypes.string,
      descriptionDateTemplate: PropTypes.string,
      details: PropTypes.arrayOf(PropTypes.string),
      linkText: PropTypes.string,
      linkUrl: PropTypes.string,
      noteText: PropTypes.string,
      uploadLinkText: PropTypes.string,
      uploadLinkUrl: PropTypes.string,
      repeatable: PropTypes.bool,
    }),
  ),
  showCurrentPhaseDate: PropTypes.bool,
  useVaTabs: PropTypes.bool,
};
